// Currency, country, timezone helpers and static data.

export const CURRENCIES = [
  { code: "USD", symbol: "$",  name: "US Dollar" },
  { code: "EUR", symbol: "€",  name: "Euro" },
  { code: "GBP", symbol: "£",  name: "British Pound" },
  { code: "CAD", symbol: "CA$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "JPY", symbol: "¥",  name: "Japanese Yen" },
  { code: "INR", symbol: "₹",  name: "Indian Rupee" },
  { code: "BRL", symbol: "R$", name: "Brazilian Real" },
  { code: "MXN", symbol: "MX$", name: "Mexican Peso" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
  { code: "ZAR", symbol: "R",  name: "South African Rand" },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc" },
  { code: "SEK", symbol: "kr", name: "Swedish Krona" },
  { code: "NOK", symbol: "kr", name: "Norwegian Krone" },
];

export const CURRENCY_MAP = Object.fromEntries(CURRENCIES.map((c) => [c.code, c]));

export const formatMoney = (amount, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: currency === "JPY" ? 0 : 2,
    }).format(amount);
  } catch {
    const c = CURRENCY_MAP[currency] || CURRENCY_MAP.USD;
    return `${c.symbol}${Number(amount).toFixed(currency === "JPY" ? 0 : 2)}`;
  }
};

// Common countries — ISO 3166-1 alpha-2 with flag emoji
export const COUNTRIES = [
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  { code: "AU", name: "Australia", flag: "🇦🇺" },
  { code: "DE", name: "Germany", flag: "🇩🇪" },
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "ES", name: "Spain", flag: "🇪🇸" },
  { code: "IT", name: "Italy", flag: "🇮🇹" },
  { code: "NL", name: "Netherlands", flag: "🇳🇱" },
  { code: "PT", name: "Portugal", flag: "🇵🇹" },
  { code: "BR", name: "Brazil", flag: "🇧🇷" },
  { code: "MX", name: "Mexico", flag: "🇲🇽" },
  { code: "AR", name: "Argentina", flag: "🇦🇷" },
  { code: "IN", name: "India", flag: "🇮🇳" },
  { code: "JP", name: "Japan", flag: "🇯🇵" },
  { code: "SG", name: "Singapore", flag: "🇸🇬" },
  { code: "AE", name: "UAE", flag: "🇦🇪" },
  { code: "ZA", name: "South Africa", flag: "🇿🇦" },
  { code: "CH", name: "Switzerland", flag: "🇨🇭" },
  { code: "SE", name: "Sweden", flag: "🇸🇪" },
  { code: "NO", name: "Norway", flag: "🇳🇴" },
  { code: "IE", name: "Ireland", flag: "🇮🇪" },
  { code: "NZ", name: "New Zealand", flag: "🇳🇿" },
];

export const COUNTRY_MAP = Object.fromEntries(COUNTRIES.map((c) => [c.code, c]));

// Country to default currency + timezone
export const COUNTRY_DEFAULTS = {
  US: { currency: "USD", tz: "America/New_York" },
  GB: { currency: "GBP", tz: "Europe/London" },
  CA: { currency: "CAD", tz: "America/Toronto" },
  AU: { currency: "AUD", tz: "Australia/Sydney" },
  DE: { currency: "EUR", tz: "Europe/Berlin" },
  FR: { currency: "EUR", tz: "Europe/Paris" },
  ES: { currency: "EUR", tz: "Europe/Madrid" },
  IT: { currency: "EUR", tz: "Europe/Rome" },
  NL: { currency: "EUR", tz: "Europe/Amsterdam" },
  PT: { currency: "EUR", tz: "Europe/Lisbon" },
  BR: { currency: "BRL", tz: "America/Sao_Paulo" },
  MX: { currency: "MXN", tz: "America/Mexico_City" },
  AR: { currency: "USD", tz: "America/Argentina/Buenos_Aires" },
  IN: { currency: "INR", tz: "Asia/Kolkata" },
  JP: { currency: "JPY", tz: "Asia/Tokyo" },
  SG: { currency: "SGD", tz: "Asia/Singapore" },
  AE: { currency: "AED", tz: "Asia/Dubai" },
  ZA: { currency: "ZAR", tz: "Africa/Johannesburg" },
  CH: { currency: "CHF", tz: "Europe/Zurich" },
  SE: { currency: "SEK", tz: "Europe/Stockholm" },
  NO: { currency: "NOK", tz: "Europe/Oslo" },
  IE: { currency: "EUR", tz: "Europe/Dublin" },
  NZ: { currency: "AUD", tz: "Pacific/Auckland" },
};

export const LANGUAGES = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "pt", name: "Português", flag: "🇵🇹" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "hi", name: "हिन्दी", flag: "🇮🇳" },
  { code: "ar", name: "العربية", flag: "🇸🇦" },
  { code: "ja", name: "日本語", flag: "🇯🇵" },
];

export const RTL_LANGS = new Set(["ar", "he", "fa", "ur"]);

// Format a datetime string in the viewer's local timezone
export const formatEventTime = (iso, tz) => {
  if (!iso) return "";
  const d = new Date(iso);
  try {
    const local = new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(d);
    return local;
  } catch {
    return d.toLocaleString();
  }
};

export const formatInEventTz = (iso, tz) => {
  if (!iso || !tz) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
      timeZone: tz,
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

// Rough distance in km between two lat/lng
export const kmBetween = (a, b) => {
  if (!a || !b) return null;
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const aa = s1 * s1 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * s2 * s2;
  return R * 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
};

// Convert a "YYYY-MM-DDTHH:mm" wall-clock time in an IANA zone to a UTC ISO string
export const zonedToUtcIso = (local, tz) => {
  if (!local) return null;
  const asUtc = new Date(`${local}:00Z`);
  if (!tz) return new Date(local).toISOString();
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })
      .formatToParts(asUtc).map((p) => [p.type, p.value])
  );
  const tzWall = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return new Date(asUtc.getTime() - (tzWall - asUtc.getTime())).toISOString();
};
